'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { User } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  showSubscriptionPrompt: boolean;
  setShowSubscriptionPrompt: (show: boolean) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, role: 'student' | 'admin') => Promise<void>;
  signOut: () => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
      /undefined.*table/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

const ADMIN_EMAIL = 'powerplantprep@gmail.com';
const ADMIN_PASSWORD = 'Igotrice4day$';

function normalizeEmail(email?: string | null) {
  return (email ?? '').trim().toLowerCase();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionPrompt, setShowSubscriptionPrompt] = useState(false);

  const router = useRouter();

  // Keep client stable across renders
  const supabase = useMemo(() => createClient(), []);

  // Prevent double redirects (SIGNED_IN + INITIAL_SESSION, rerenders, etc.)
  const redirectedRef = useRef(false);

  const routeForSession = async (sessionUser: User) => {
    const sessionEmail = normalizeEmail(sessionUser.email);
    const isAdminLogin = sessionEmail === normalizeEmail(ADMIN_EMAIL);

    if (!sessionUser.email_confirmed_at) {
      router.push('/verify-email');
      router.refresh();
      redirectedRef.current = true;
      return;
    }

    if (isAdminLogin) {
      router.push('/admin-dashboard');
      router.refresh();
      redirectedRef.current = true;
      return;
    }

    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', sessionUser.id)
      .single();

    if (error && isSchemaError(error)) {
      console.error('Schema error:', error.message);
      throw error;
    }

    if (profile?.role === 'student') setShowSubscriptionPrompt(true);

    router.push(profile?.role === 'admin' ? '/admin-dashboard' : '/student-dashboard');
    router.refresh();
    redirectedRef.current = true;
  };

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error && isSchemaError(error)) throw error;
        if (mounted) setUser(data.user ?? null);
      } catch (error: any) {
        if (isSchemaError(error)) console.error('Schema error:', error.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);

      // This is the common “land on admin then get kicked” scenario:
      // the page loads, then INITIAL_SESSION fires and your logic routes again.
      if (!session?.user) return;
      if (redirectedRef.current) return;

      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        try {
          await routeForSession(session.user);
        } catch (e) {
          // avoid infinite loops; surface to console
          console.error(e);
        }
      }
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, [supabase, router]);

  const signIn = async (email: string, password: string) => {
    const emailNorm = normalizeEmail(email);
    const isAdminLogin = emailNorm === normalizeEmail(ADMIN_EMAIL);

    try {
      redirectedRef.current = false;

      // ⚠️ Security note: hardcoding admin password in client is unsafe.
      if (isAdminLogin && password !== ADMIN_PASSWORD) {
        throw new Error('Invalid email or password');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: emailNorm,
        password,
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      if (!data.user?.email_confirmed_at) {
        router.push('/verify-email');
        router.refresh();
        return;
      }

      setUser(data.user);

      // Route immediately (don’t wait for listener)
      await routeForSession(data.user);
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      throw error;
    }
  };

  const signUp = async (email: string, password: string, fullName: string, role: 'student' | 'admin') => {
    try {
      const { error } = await supabase.auth.signUp({
        email: normalizeEmail(email),
        password,
        options: {
          data: {
            full_name: fullName,
            role,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      router.push('/verify-email');
      router.refresh();
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      throw error;
    }
  };

  const signOut = async () => {
    try {
      redirectedRef.current = false;
      const { error } = await supabase.auth.signOut();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      setUser(null);
      router.push('/sign-up-login');
      router.refresh();
    } catch (error: any) {
      if (isSchemaError(error)) throw error;
      throw error;
    }
  };

  const resendVerificationEmail = async () => {
    const { data } = await supabase.auth.getUser();
    const user = data.user;

    if (!user?.email) throw new Error('No user email found');

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: user.email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        showSubscriptionPrompt,
        setShowSubscriptionPrompt,
        signIn,
        signUp,
        signOut,
        resendVerificationEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

